import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { LocalizedDatePipe } from 'src/app/angular-core/utils/pipes/localized-date/localized-date.pipe';
import { BasicXpacInfo, RemoteMaintenanceConnection } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { getRemoteConnectionsAction } from 'src/app/store/actions/devices.actions';
import { selectRemoteConnections } from 'src/app/store/selectors/devices.selectors';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { DurationPrecision, getDurationString } from 'src/app/utils/functions/date.functions';
import { IRemoteConnectionsState, IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';

@UntilDestroy()
@Component({
  selector: 'xpac-service-connections',
  templateUrl: './service-connections.component.html',
  styleUrls: ['./service-connections.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceConnectionsComponent implements OnInit {
  @Input() public xpac: BasicXpacInfo = null;

  public isLoading: boolean = true;
  public connections: RemoteMaintenanceConnection[] = [];

  constructor(
    private store: Store<IRemoteXPACPortalState>,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private datePipe: LocalizedDatePipe,
  ) { }

  public ngOnInit(): void {     
    this.initListners();    
  }

  public getTitle(connection: RemoteMaintenanceConnection): string {
    return this.getUserDisplayName(connection);
  }

  public getInfo(connection: RemoteMaintenanceConnection): string {
    const duration: string = this.getDurationString(connection);
    return `${this.datePipe.transform(connection.startUtc, 'short')} - ${duration} - ${connection.remoteIP}`;
  }

  public getUserDisplayName(connection: RemoteMaintenanceConnection): string {
    // If for some reason the user isn't available we'll just have to show the IP
    return connection.user?.name ?? connection.remoteIP;
  }

  private getDurationString(connection: RemoteMaintenanceConnection): string{
    let duration: string = this.translate.instant('General.NotAvailableShort');

    if(connection.endUtc && connection.startUtc){
      const uptime = (Date.parse(connection.endUtc) - Date.parse(connection.startUtc)) / 1000;
      duration = getDurationString(uptime, this.translate, DurationPrecision.Minutes);
    }

    return duration;
  }

  private initListners(): void{
    this.store.dispatch(getRemoteConnectionsAction({ data: this.xpac.deviceId }));

    this.store.select(selectRemoteConnections(this.xpac.deviceId))
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe((connections: IRemoteConnectionsState) => {
        this.connections = connections.connections;
        this.isLoading = false;
        this.cdr.markForCheck();
      });
  }
}
